import type {
  ToolCallMessageContent,
  ToolResultMessageContent,
} from "schemas/chat";
import type { ChatMessage } from "schemas/generated/chatMessage";

import React from "react";

import { Button } from "@replo/design-system/components/button/Button";
import { Popover } from "@replo/design-system/components/popover/Popover";
import { Tooltip } from "@replo/design-system/components/tooltip/Tooltip";
import ToolCallRenderer from "@v2/features/chat/components/chat-elements/chat-message/ToolCallRenderer";
import { useChatId } from "@v2/features/chat/hooks/useChatId";
import { useMessagesForChat } from "@v2/features/chat/store/ChatStoreContext";
import { findToolResult } from "@v2/features/chat/utils/findToolResult";
import { isDevelopment } from "@v2/utils/env";
import shouldShowV2Debuggers from "@v2/utils/shouldShowV2Debuggers";
import { ArrowUpRight, MoreHorizontal } from "lucide-react";
import { toolNameSchema } from "schemas/chatMessage";

const DebugDataDisplay: React.FC<{
  title: string;
  data: unknown;
}> = ({ title, data }) => {
  if (!data) {
    return (
      <div>
        <div className="font-medium text-xs text-default mb-2">{title}</div>
        <div className="text-2xs text-muted-foreground italic">No data</div>
      </div>
    );
  }

  return (
    <div>
      <div className="font-medium text-xs text-default mb-2">{title}</div>
      <div className="flex flex-col gap-2">
        {Object.entries(data).map(([key, value]) => (
          <div key={key}>
            <div className="text-2xs font-medium text-muted-foreground mb-1">
              {key}
            </div>
            <pre className="bg-neutral-soft rounded p-2 text-2xs break-words whitespace-pre-wrap border border-muted max-w-full max-h-[300px] overflow-auto">
              {typeof value === "object"
                ? JSON.stringify(value, null, 2)
                : String(value)}
            </pre>
          </div>
        ))}
      </div>
    </div>
  );
};

const ToolDebugWrapper = ({
  toolCall,
  toolResult,
  messageId,
  children,
}: React.PropsWithChildren<{
  toolCall: ToolCallMessageContent;
  toolResult: ToolResultMessageContent | null;
  messageId?: string;
}>) => {
  const isDebug = shouldShowV2Debuggers();

  const adminUrl = messageId
    ? `https://admin.replo.app/dashboard/chat-messages?chatMessageId=${messageId}`
    : null;

  const triggerJobUrl =
    toolResult?.toolName === "replicationTool" && toolResult.output.value.runId
      ? `https://cloud.trigger.dev/orgs/replo-7289/projects/test-quaR/env/${isDevelopment ? "dev" : "prod"}/runs/${toolResult.output.value.runId}`
      : null;

  return (
    <div className="flex flex-row justify-between items-start gap-2">
      {children}
      {isDebug && (
        <Popover
          side="right"
          align="start"
          sideOffset={8}
          UNSAFE_className="w-[500px]"
          title={`Debug ${toolCall.toolName}`}
          trigger={
            <Tooltip content={`Debug ${toolCall.toolName}`}>
              <Button icon variant="tertiary" size="sm">
                <MoreHorizontal />
              </Button>
            </Tooltip>
          }
        >
          <div className="flex flex-col gap-4 h-full max-h-[600px] overflow-y-auto">
            <DebugDataDisplay title="Tool Call Input" data={toolCall.input} />
            {toolResult && (
              <DebugDataDisplay
                title="Tool Result"
                data={toolResult.output.value}
              />
            )}
            <div className="flex flex-row gap-2">
              {adminUrl && (
                <Button
                  variant="primary"
                  onClick={() => {
                    window.open(adminUrl, "_blank");
                  }}
                  endEnhancer={<ArrowUpRight />}
                >
                  View in Admin
                </Button>
              )}
              {triggerJobUrl && (
                <Button
                  variant="primary"
                  onClick={() => {
                    window.open(triggerJobUrl, "_blank");
                  }}
                  endEnhancer={<ArrowUpRight />}
                >
                  Trigger Job
                </Button>
              )}
            </div>
          </div>
        </Popover>
      )}
    </div>
  );
};

interface ToolCallMessageItemProps {
  toolCall: ToolCallMessageContent;
  messages?: ChatMessage[];
  isActivelyStreaming?: boolean;
}

export default function ToolCallMessageItem({
  toolCall,
  messages,
  isActivelyStreaming = false,
}: ToolCallMessageItemProps) {
  const chatId = useChatId();
  const allMessages = useMessagesForChat(chatId);
  const { toolResult } = findToolResult(toolCall.toolCallId, allMessages);

  // Find the message ID that contains this tool call
  const messageId = allMessages.find((msg) => {
    return msg.content.some((content) => {
      return (
        content.type === "tool-call" &&
        content.toolCallId === toolCall.toolCallId
      );
    });
  })?.id;

  if (
    !shouldShowV2Debuggers() &&
    !toolNameSchema.safeParse(toolCall.toolName).success
  ) {
    return null;
  }

  return (
    <div className="text-sm w-full min-w-0">
      <ToolDebugWrapper
        toolCall={toolCall}
        toolResult={toolResult}
        messageId={messageId}
      >
        <ToolCallRenderer
          toolCall={toolCall}
          toolResult={toolResult}
          messages={messages}
          isActivelyStreaming={isActivelyStreaming}
        />
      </ToolDebugWrapper>
    </div>
  );
}
